import React, { useState } from "react";
import style from "./TextAreaField.module.scss";

const cx = (...classNames: (string | undefined | false | null)[]) =>
  classNames.filter(Boolean).join(" ");

interface TextAreaFieldClassNames {
  root?: string;
  label?: string;
  textareaWrapper?: string;
  textarea?: string;
  error?: string;
  helperText?: string;
  charCount?: string;
}

interface RegisterResult {
  name?: string;
  ref?: React.Ref<HTMLTextAreaElement>;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
}

interface TextAreaFieldProps {
  id?: string;
  name?: string;
  label?: React.ReactNode;
  value?: string;
  defaultValue?: string;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
  onKeyUp?: React.KeyboardEventHandler<HTMLTextAreaElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement>;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  rows?: number;
  charLimit?: number;
  error?: string;
  helperText?: React.ReactNode;
  hint?: React.ReactNode;
  className?: string;
  classNames?: TextAreaFieldClassNames;
  /** RHF v7 register result: `{ name, ref, onChange, onBlur }` */
  register?: RegisterResult;
  /** RHF v6 / ref-callback style */
  inputRef?: React.Ref<HTMLTextAreaElement>;
  dataTestId?: string;
}

/**
 * TextAreaField — the primary multi-line text input primitive.
 *
 * API mirrors InputField so the two can be used interchangeably in forms.
 * Replaces the legacy `TextArea` (`src/components/primary/TextArea/textArea.js`),
 * which coupled layout classes and relied on Bootstrap utilities. This component
 * is self-contained: layout, focus ring, error state, and char counter are all
 * in TextAreaField.module.scss using design tokens from standard.module.scss.
 *
 * RHF compatibility:
 *   - v7 style: pass `register={register("fieldName")}` — spread onto the textarea.
 *   - v6 style: pass `inputRef={register(rules)}` — forwarded as a ref.
 *   - Controlled: use `value` + `onChange` directly.
 */
export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  id,
  name,
  label,
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  onKeyUp,
  onKeyDown,
  disabled = false,
  required = false,
  readOnly = false,
  placeholder = "",
  rows = 4,
  charLimit,
  error,
  helperText,
  hint,
  className = "",
  classNames = {},
  register,
  inputRef,
  dataTestId,
}) => {
  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? "");
  const displayValue = isControlled ? value : uncontrolledValue;

  const errorId = error && id ? `${id}-error` : undefined;
  const helperId = (helperText || hint) && id ? `${id}-helper` : undefined;
  const describedBy =
    [errorId, helperId].filter(Boolean).join(" ") || undefined;
  const helperMessage = helperText || hint;
  const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (event) => {
    if (!isControlled) {
      setUncontrolledValue(event.target.value);
    }
    (register?.onChange ?? onChange)?.(event);
  };

  return (
    <div className={cx(style.root, className, classNames.root)}>
      {label && (
        <label
          htmlFor={id}
          className={cx(style.label, classNames.label)}
        >
          {label}{" "}
          {required ? <span className={style.astrick}>&#42;</span> : ""}
        </label>
      )}

      <div
        className={cx(
          style.textareaWrapper,
          error && style.textareaWrapperError,
          disabled && style.textareaWrapperDisabled,
          classNames.textareaWrapper
        )}
      >
        <textarea
          id={id}
          name={register?.name ?? name}
          value={isControlled ? value : undefined}
          defaultValue={isControlled ? undefined : defaultValue}
          onChange={handleChange}
          onBlur={register?.onBlur ?? onBlur}
          onFocus={onFocus}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          disabled={disabled}
          required={required}
          readOnly={readOnly}
          placeholder={placeholder}
          rows={rows}
          maxLength={charLimit}
          ref={inputRef ?? register?.ref}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          data-testid={dataTestId || id}
          className={cx(style.textarea, classNames.textarea)}
        />
      </div>

      {(charLimit || error || helperMessage) && (
        <div className={style.footer}>
          <span>
            {error && (
              <span
                id={errorId}
                className={cx(style.errorText, classNames.error)}
              >
                {error}
              </span>
            )}
            {!error && helperMessage && (
              <span
                id={helperId}
                className={cx(style.helperText, classNames.helperText)}
              >
                {helperMessage}
              </span>
            )}
          </span>
          {charLimit && (
            <span className={cx(style.charCount, classNames.charCount)}>
              {displayValue ? String(displayValue).length : 0}/{charLimit}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
